/**
 * ai-model-picker-lib.ts — Phần THUẦN của ô chọn model dùng chung (`ai-model-picker.tsx`):
 * nhóm model theo nền tảng kèm nguồn (AI của eCDP / Key của tôi), khoá model theo GÓI (`allowed=false` từ /ai/models)
 * và theo NGUỒN (Key của tôi chưa có key, AI của eCDP chưa có cho nền tảng), nhãn từng dòng, dòng thông tin dưới ô.
 */
import { SUPPORTED_AI_PROVIDERS } from '@/lib/ai-providers'
import { i18n } from '@/lib/i18n'
import {
  formSourceInfo, perReplyText, SOURCE_LABEL,
  type AiCredits, type AiModelEntry, type AiProviderSource, type AiSource, type SourceInfo,
} from '@/lib/ai-credits'

export interface PickerValue {
  provider: string | null
  model: string | null
}

export interface PickerOption {
  /** "provider:model" */
  value: string
  provider: string
  model: string
  label: string
  disabled: boolean
  lockReason: string | null
  source: AiSource
  creditsPerTurn: number | null
}

export interface PickerGroup {
  provider: string
  label: string
  source: AiSource | null
  options: PickerOption[]
}

export const encodePickerValue = (v: PickerValue | null | undefined): string =>
  v?.provider && v?.model ? `${v.provider}:${v.model}` : ''

export function decodePickerValue(s: string): PickerValue | null {
  if (!s) return null
  const i = s.indexOf(':')
  if (i <= 0) return null
  return { provider: s.slice(0, i), model: s.slice(i + 1) }
}

/** Lý do khoá một model (null = chọn được): gói trước, rồi nguồn. */
export function pickerLock(m: AiModelEntry, src?: Pick<AiProviderSource, 'source' | 'byok' | 'platformAvailable'> | null): string | null {
  if (!m.allowed) return m.lockReason || i18n.t('aiComp:modelPickerLib.notAllowed')
  if (m.source === 'byok' && src && !src.byok?.configured) return i18n.t('aiComp:modelPickerLib.noKey')
  if (m.source === 'platform' && src && src.platformAvailable === false) return i18n.t('aiComp:modelPickerLib.noPlatformAi')
  return null
}

/** "GPT-5.4 Mini · ≈ 1 lượt / câu trả lời" / "· Key của tôi" / "· 🔒 Cần gói trả phí". */
export function pickerOptionLabel(m: AiModelEntry, lock: string | null): string {
  if (lock) return `${m.title} · 🔒 ${lock}`
  if (m.source === 'byok') return i18n.t('aiComp:modelPickerLib.byokOption', { title: m.title })
  const per = perReplyText(m)
  return per ? `${m.title} · ${per}` : i18n.t('aiComp:modelPickerLib.platformOption', { title: m.title })
}

/** Nhóm theo nền tảng (ChatGPT, Claude, Gemini, Grok, DeepSeek trước; nền tảng lạ sau). */
export function buildPickerGroups(
  catalog: AiModelEntry[],
  sources: Array<Pick<AiProviderSource, 'provider' | 'name' | 'source' | 'byok' | 'platformAvailable'>> | null | undefined,
): PickerGroup[] {
  const ids = [
    ...SUPPORTED_AI_PROVIDERS.filter((id) => catalog.some((m) => m.provider === id)),
    ...[...new Set(catalog.map((m) => m.provider))].filter((id) => !(SUPPORTED_AI_PROVIDERS as readonly string[]).includes(id)),
  ]
  return ids.map((pid) => {
    const src = sources?.find((s) => s.provider === pid) ?? null
    const name = src?.name ?? pid
    const source = src?.source ?? catalog.find((m) => m.provider === pid)?.source ?? null
    return {
      provider: pid,
      label: `${name}${source ? ` — ${SOURCE_LABEL[source]}` : ''}`,
      source,
      options: catalog.filter((m) => m.provider === pid).map((m) => {
        const lock = pickerLock(m, src)
        return {
          value: `${pid}:${m.model}`, provider: pid, model: m.model,
          label: pickerOptionLabel(m, lock), disabled: !!lock, lockReason: lock,
          source: m.source, creditsPerTurn: m.creditsPerTurn,
        }
      }),
    }
  })
}

/** Tìm lựa chọn đang chọn (undefined = giá trị không còn trong danh mục). */
export function findPickerOption(groups: PickerGroup[], value: PickerValue | null | undefined): PickerOption | undefined {
  const v = encodePickerValue(value)
  if (!v) return undefined
  for (const g of groups) {
    const o = g.options.find((x) => x.value === v)
    if (o) return o
  }
  return undefined
}

/**
 * Dòng thông tin dưới ô: model đang chọn bị khoá → nói lý do; còn lại → nguồn + ≈ lượt AI / câu trả lời + lượt còn lại
 * (formSourceInfo).
 * `null` khi chưa chọn gì / không có danh mục.
 */
export function pickerInfo(opts: {
  value: PickerValue | null | undefined
  catalog: AiModelEntry[] | null | undefined
  sources: Array<Pick<AiProviderSource, 'provider' | 'name' | 'source' | 'byok' | 'platformAvailable'>> | null | undefined
  credits?: Pick<AiCredits, 'balance' | 'monthlyGrant' | 'status'> | null
}): SourceInfo | null {
  const { value, catalog, sources, credits } = opts
  if (!catalog || !value?.provider || !value.model) return null
  const m = catalog.find((x) => x.provider === value.provider && x.model === value.model)
  const src = sources?.find((s) => s.provider === value.provider) ?? null
  if (!m) return { tone: 'warn', text: i18n.t('aiComp:modelPickerLib.gone', { model: value.model }) }
  const lock = pickerLock(m, src)
  if (lock) return { tone: 'warn', text: i18n.t('aiComp:modelPickerLib.locked', { lock }) }
  return formSourceInfo({ model: m, byok: src?.byok ?? null, credits: credits ?? null, providerName: src?.name ?? m.provider })
}
